import { log } from "console"
import { useDebouncedCallback } from "use-debounce"
import endpoints from "@config/endpoints"
import api from "api"
import cloneDeep from "lodash/cloneDeep"
import qs from "qs"
import { useEffect, useMemo } from "react"
import { useInboxStore } from "stores/inboxStore"
import { FetchTeeupsArgs, FilterTypes } from "types/inbox"
import { shallow } from "zustand/shallow"
import { setTeeupActive } from "@actions/activeTeeupActions"
import { initialTeeup } from "@config/enums"
import { useSelector } from "react-redux"
import { selectUserId } from "@selectors/user"
import { formatTeeups, teeupSorting } from "@utils/inbox"
import { GameplanItem, TeeupItem } from "types/teeup"
import { selectCalendarEvents } from "@selectors/calendar"
import { conversationTypeIds } from "@config/mappings"
import { isEmpty } from "lodash"

interface WorkerMessage {
  withConflicts: Record<number, GameplanItem[]>
  original: Record<number, GameplanItem[]>
}

const FETCH_DELAY = 500

export const useFetchTeeUps = () => {
  const teeUps = useInboxStore(state => state.teeUps)
  const teeUpsState = useInboxStore(state => state.teeUpsState)
  const teeupParticipants = useInboxStore(state => state.teeupParticipants)
  const calendarEvents = useSelector(selectCalendarEvents)
  const currentSortBy = useInboxStore(state => state.currentSortBy)
  const tempGameplanOptions = useInboxStore(state => state.tempGameplanOptions)

  const setTeeUps = useInboxStore(state => state.setTeeUps)
  const setPagination = useInboxStore(state => state.setPagination)
  const setIsLoading = useInboxStore(state => state.setIsLoading)
  const setIsFetchError = useInboxStore(state => state.setIsFetchError)
  const setTeeupParticipants = useInboxStore(
    state => state.setTeeupParticipants
  )
  const setTeeUpState = useInboxStore(state => state.setTeeUpsState)
  const setGameplanOptions = useInboxStore(state => state.setGameplanOptions)
  const setNudges = useInboxStore(state => state.setNudges)
  const setTempGameplanOptions = useInboxStore(
    state => state.setTempGameplanOptions
  )

  const userId = useSelector(selectUserId)

  const worker = useMemo(
    () =>
      new Worker(new URL("./worker_checkCalendarConflicts", import.meta.url)),
    []
  )

  const handleWorkerGameplanOptions = (e: MessageEvent<WorkerMessage>) => {
    const gameplanOptions = useInboxStore.getState().gameplanOptions
    const withConflicts = e.data?.withConflicts
    const original = e.data?.original
    console.log("handleWorkerGameplanOptions", { withConflicts, original })

    // INBOX-REBUILD gap-fix 2026-07-03 (root cause of EVERY inbox card rendering "---" for
    // When/Where whenever ANY calendar conflict exists anywhere in the list): when the worker
    // (worker_checkCalendarConflicts.ts) finds >=1 conflicting decided suggestion, its
    // `withConflicts` branch returns a SPARSE map containing ONLY the conflicted teeups (the
    // reduce sets acc[key] just for matches). The old `withConflicts || original` stored that
    // sparse map alone, silently dropping every non-conflicted teeup's gameplan options -- so
    // decided When/Where lines AND Planning-suggestion pills vanished from all other cards.
    // Merge original FIRST, then overlay the conflict-annotated entries on top.
    setGameplanOptions({
      ...gameplanOptions,
      ...(original || {}),
      ...(withConflicts || {}),
    })
  }

  const handleNudgesAndMentions = (
    messages: Record<number, any[]>,
    userId: number
  ) => {
    const ignoredMentions = localStorage.getItem("ignoredMentions")
    const ignoredNudges = localStorage.getItem("ignoredNudges")

    const nudges = Object.entries(messages).reduce(
      (acc, [teeupId, messages]) => {
        let filteredMentions: any[] = []
        let filteredNudges: any[] = []

        const mentions = messages?.filter?.(
          message =>
            message.conversationTypeId === conversationTypeIds.message &&
            message?.mentionIds?.includes(userId)
        )
        const nudgesList = messages?.filter?.(
          message => message.conversationTypeId === conversationTypeIds.nudge
        )

        if (ignoredMentions) {
          const ignoredMentionsList = JSON.parse(ignoredMentions)
          filteredMentions = mentions.filter(
            message => !ignoredMentionsList.includes(message.id)
          )
        }

        if (ignoredNudges) {
          const ignoredNudgesList = JSON.parse(ignoredNudges)
          filteredNudges = nudgesList.filter(
            message => !ignoredNudgesList.includes(message.id)
          )
        }

        acc[teeupId] = { mentions: [], nudges: [] }
        acc[teeupId].mentions =
          filteredMentions.length > 0 || ignoredMentions?.length
            ? filteredMentions
            : mentions
        acc[teeupId].nudges =
          filteredNudges.length > 0 || ignoredNudges?.length
            ? filteredNudges
            : nudgesList

        return acc
      },
      {}
    )
    setNudges(nudges)
  }

  const prepareQueryParams = (
    {
      selectedFilters,
      currentOpenTab,
      currentPage,
      searchValue: search,
    }: FetchTeeupsArgs,
    shouldClearPrevState: boolean
  ) => {
    const teeUpStageFilters = selectedFilters[FilterTypes.stage]
    const teeUpUserStatusFilters = selectedFilters[FilterTypes.userStatus]

    const filterByUserStatus = teeUpUserStatusFilters.map(item => item.id)
    const filterByTeeupStage = teeUpStageFilters.map(item => item.id)

    const userStatusFilter =
      currentOpenTab?.filterQuery === "invited" ? "invited" : "all"
    const teeupStatusFilter =
      currentOpenTab?.filterQuery === "ended" ? "ended" : "all"

    const query = {
      page: shouldClearPrevState ? 0 : currentPage,
      limit: 15,
      participants: true,
      gameplanOptions: true,
      states: true,
      messages: true,
      // TODO: if "remake: true/false" included - filtration not working on backend
      // remake: false,

      category: currentOpenTab?.requestType,
      scheduleFilter: currentOpenTab?.scheduleFilter,

      userStatusFilter,
      teeupStatusFilter,

      teeupStatus: filterByTeeupStage,
      userStatus: filterByUserStatus,

      search,
    }

    return qs.stringify(query)
  }

  const fetchPaginatedTeeups = useDebouncedCallback(
    async (state: FetchTeeupsArgs, shouldClearPrevState: boolean) => {
      if (!api?.client) return setIsLoading(false)

      try {
        let sortedTeeUps: TeeupItem[] | null = null
        const params = prepareQueryParams(state, shouldClearPrevState)
        const url = `${endpoints.paginatedTeeups}?${params}`

        const response = await api.client.get(url)
        const data = response?.data

        handleNudgesAndMentions(data?.messages, userId)

        const formattedTeeups = formatTeeups(data?.teeups, data?.participants)

        const teeUpsCollected = [...teeUps, ...formattedTeeups]

        if (shouldClearPrevState) {
          sortedTeeUps = teeupSorting(teeUpsCollected, currentSortBy, userId)
        }

        setTempGameplanOptions({
          ...tempGameplanOptions,
          ...data?.gameplanOptions,
        })
        setTeeUps(sortedTeeUps || teeUpsCollected)
        setTeeUpState({ ...teeUpsState, ...data?.states })
        setPagination({
          total: data?.total,
          page: data?.page,
          nextPage: data?.nextPage,
          previousPage: data?.previousPage,
        })
        setTeeupParticipants({
          ...teeupParticipants,
          ...data?.participants,
        })
        // BUG4 fix: clear a prior error once a fetch actually succeeds -- this flag was
        // otherwise write-only (set true on failure, never reset), so after one failed
        // request every subsequent render (even a later successful one, if something else
        // re-read the stale flag) would still be error-shaped.
        setIsFetchError(false)
      } catch (e) {
        console.log("error", e)
        setIsFetchError(true)
      } finally {
        setIsLoading(false)
      }
    },
    FETCH_DELAY
  )

  const subscribeFiltration = () => {
    const unSubscribe = useInboxStore.subscribe(
      state => ({
        currentOpenTab: state.currentOpenTab,
        selectedFilters: state.selectedFilters,
        searchValue: state.searchValue,
        currentPage: state.pagination.page,
        triggerTeeupReload: state.triggerTeeupReload,
      }),
      (selectedState, prevState) => {
        setIsLoading(true)

        const shouldClearPrevState =
          selectedState.currentPage === prevState.currentPage

        if (shouldClearPrevState) {
          setTeeUps([])
        }
        fetchPaginatedTeeups(selectedState, shouldClearPrevState)
      },
      {
        equalityFn: shallow,
        fireImmediately: true,
      }
    )

    return unSubscribe
  }

  const subscribeSorting = () => {
    const unSubscribe = useInboxStore.subscribe(
      state => ({ currentSortBy: state.currentSortBy }),
      ({ currentSortBy }) => {
        const teeUps = useInboxStore.getState().teeUps

        const sortedTeeUps = teeupSorting(teeUps, currentSortBy, userId)

        setTeeUps(cloneDeep(sortedTeeUps))
      },
      {
        equalityFn: shallow,
        fireImmediately: false,
      }
    )

    return unSubscribe
  }

  useEffect(() => {
    // NOTE: setTeeupActive(initialTeeup) was removed from here (2026-07-01 fix/round1-web).
    // Calling it inside this shared hook caused TeeupPageContent (detail page) to reset
    // activeTeeup.id=null on every mount, creating a 10Hz re-fetch loop on the detail view.
    // The reset now lives in TeeUpsNd (the inbox list component) where it belongs: it only
    // needs to fire when entering the inbox, not whenever any consumer of this hook mounts.

    worker.onmessage = e => {
      handleWorkerGameplanOptions(e)
    }

    const unSubscribePagination = subscribeFiltration()
    const unSubscribeSorting = subscribeSorting()

    return () => {
      unSubscribePagination()
      unSubscribeSorting()
    }
  }, [])

  useEffect(() => {
    const gameplanOptions = useInboxStore.getState().tempGameplanOptions

    if (isEmpty(gameplanOptions)) return

    worker.postMessage(JSON.stringify({ gameplanOptions, calendarEvents }))
  }, [calendarEvents, tempGameplanOptions])

  // RC3-001: the dev backend emits no inbox socket events, so new invites / state changes only
  // appear after a manual reload. Refetch the inbox when the tab/window regains focus (and on
  // visibility change) so returning to the app surfaces changes without a reload. Reuses the
  // existing clear-then-fetch reload path; focus-only (not a constant poll) to avoid list flicker.
  useEffect(() => {
    // BUG4 fix (false empty-state on nav/tab return): this reload path cleared `teeUps` to []
    // WITHOUT flagging `isLoading`, unlike subscribeFiltration's listener above (which always
    // does setIsLoading(true) before setTeeUps([])). TeeUpList's render gate is
    // `if (!isLoading && teeUps.length === 0) return <Empty />` -- so for the whole debounced
    // FETCH_DELAY (500ms) window between this clear and fetchPaginatedTeeups's `finally {
    // setIsLoading(false) }`, isLoading was still whatever it was left at (false, since the
    // prior fetch had already completed) while teeUps was []: exactly the loading-vs-empty
    // conflation QJ reported (Empty rendering while data was actually in flight). Setting
    // isLoading BEFORE clearing closes that window the same way the other reload path does.
    const reload = () => {
      const s = useInboxStore.getState()
      setIsLoading(true)
      setTeeUps([])
      fetchPaginatedTeeups(
        {
          currentOpenTab: s.currentOpenTab,
          selectedFilters: s.selectedFilters,
          searchValue: s.searchValue,
          currentPage: s.pagination.page,
          triggerTeeupReload: s.triggerTeeupReload,
        },
        true
      )
    }
    const onVisible = () => {
      if (document.visibilityState === "visible") reload()
    }
    window.addEventListener("focus", reload)
    document.addEventListener("visibilitychange", onVisible)
    return () => {
      window.removeEventListener("focus", reload)
      document.removeEventListener("visibilitychange", onVisible)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { fetchPaginatedTeeups, handleNudgesAndMentions }
}
